import { useEffect, useState } from 'react';

import { NotificationsResponse } from 'config/requests/notifications';
import throttle from 'lodash/throttle';
import { useNotify } from 'react-admin';

import { useChangeViewedStatus } from './useChangeViewedStatus';

type Props = {
  notifications?: NotificationsResponse;
  onSuccess: () => void;
};

export function useIntersectionChangeViewedStatus({ notifications, onSuccess }: Props) {
  const notify = useNotify();
  const [listElement, setListElement] = useState<HTMLUListElement | null>(null);

  const { changeViewedStatus } = useChangeViewedStatus({
    onSuccess,
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  useEffect(() => {
    if (!listElement || !notifications?.length) {
      return;
    }

    const visibleIds = new Set<string>();

    const changeVisibleNotificationsStatus = throttle(() => {
      const unviewedVisibleIds = Array.from(visibleIds).filter((id) => {
        const n = notifications.find((n) => String(n.id) === id);
        return n && !n.isViewed;
      });

      if (unviewedVisibleIds.length > 0) {
        changeViewedStatus(unviewedVisibleIds);
      }
    }, 300);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute('data-id');
          if (!id) {
            continue;
          }

          if (entry.isIntersecting) {
            visibleIds.add(id);
          } else {
            visibleIds.delete(id);
          }
        }

        changeVisibleNotificationsStatus();
      },
      {
        root: listElement,
        threshold: 0.5,
      },
    );

    const items = listElement.querySelectorAll('[data-id]');
    items.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      changeVisibleNotificationsStatus.cancel();
    };
  }, [listElement, notifications]);

  return setListElement;
}
